import { toBlob } from 'html-to-image';
import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Kit, MssDataPoint, TinyAccount } from '@repo/types';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useMssData } from 'services/mss/useMssData';

import checkIsKitPro from '@repo/utils/checkIsKitPro';
import {
  ButtonColors,
  ButtonVariants,
  Card,
  DesignButton,
  TextLink,
  Typography,
} from 'components';
import { CardErrorBoundary } from 'components/Card/CardErrorBoundary';
import MicrobiomeScoreModal from 'views/Results/Summary/SummaryCards/MicrobiomeScoreSummaryCard/MicrobiomeScoreModal';
import MicrobiomeScoreShareImage from 'views/Results/Summary/SummaryCards/MicrobiomeScoreSummaryCard/MicrobiomeScoreShareImage';

import { getAgeGroup, getMicrobiomeScoreMessage } from '@repo/utils';
import c from 'classnames';
import { TinyAIPromptEntryPoint } from 'components/TinyAI/TinyAIPromptEntryPoint';
import GaugeChartSection from './GaugeChartSection';
import MicrobiomeScoreChartContainer from './MicrobiomeScoreChartContainer';
import styles from './MicrobiomeScoreSummaryCard.module.css';

export const getPopScoreInfo = (
  kit?: Kit,
  account?: TinyAccount | null,
  popScore?: number | null,
  chartData?: MssDataPoint[],
) => {
  if (!kit || !popScore || !chartData)
    return { description: '', percentile: 1 };
  const { sampling_type, age_at_sampling } = kit;
  if (!sampling_type || !age_at_sampling) {
    return { description: '', percentile: 0 };
  }
  const isBaby = age_at_sampling < 365;
  const popScoreData = chartData.find(data => data.score === popScore);
  const color = popScoreData?.color;

  const isAdult = getAgeGroup(kit.age_at_sampling ?? 0) === 'adult';
  const name = account?.first_name ?? '';
  const { mainText: description } = getMicrobiomeScoreMessage({
    color,
    isAdult,
    name,
  });

  let ageText = '';
  if (isBaby) {
    ageText = 'babies aged 0-1yr';
  } else if (age_at_sampling < 1095) {
    ageText = 'toddlers aged 1-3yrs';
  } else if (age_at_sampling < 6570) {
    ageText = 'children aged 3-18yrs';
  } else {
    ageText = 'adults 18+';
  }
  return {
    description,
    percentile: Math.max(1, popScoreData?.ab_perc ?? 1),
    color,
    ageText,
  };
};
export const OffScreenContainer = ({
  children,
  ...props
}: React.PropsWithChildren) => (
  <div className={styles.offScreenContainer} {...props}>
    {children}
  </div>
);
export const MicrobiomeScoreSummaryCard = () => {
  const { currentKit, currentTinyAccount } = useCurrentKitAndTinyAccount();
  const { trackEvent } = useAnalytics();
  const imageRef = useRef<HTMLDivElement>(null);
  const { popGutScore, mssData: chartData } = useMssData(currentKit?.id);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { description, color } = getPopScoreInfo(
    currentKit,
    currentTinyAccount,
    popGutScore,
    chartData,
  );
  const toggleModal = () => {
    if (!isModalOpen) {
      trackEvent('About MSS Score Clicked');
    }
    setIsModalOpen(!isModalOpen);
  };

  const isKitPro = checkIsKitPro(currentKit);

  const onShareClick = async () => {
    trackEvent('Share Clicked', { location: 'results' });
    trackEvent('MSS Share Clicked');
    if (!imageRef.current) return;
    const newFile = await toBlob(imageRef.current as HTMLElement, {
      skipFonts: true,
      pixelRatio: 2,
    });

    if (!newFile) return;
    const data = {
      files: [
        new File([newFile], 'tinyhealth_microbiome_summary_score.png', {
          type: 'image/png',
          lastModified: Date.now(),
        }),
      ],
      title: 'tinyhealth_microbiome_summary_score.png',
      text: 'Check out my microbiome health score from Tiny Health! Find out yours at www.tinyhealth.com',
    };
    try {
      if (!navigator?.canShare?.(data)) {
        navigator.clipboard.write([
          new ClipboardItem({
            'image/png': newFile,
          }),
        ]);
        toast.success('Image copied to clipboard');
      } else {
        await navigator.share(data);
      }
    } catch (err) {
      console.error(err);
    }
  };
  if (!currentKit || !popGutScore) return <></>;

  return (
    <CardErrorBoundary>
      <>
        <Card>
          <div className={styles.cardContent}>
            <MicrobiomeScoreModal
              isOpen={isModalOpen}
              onClose={toggleModal}
              populationScore={popGutScore}
              color={color}
            />
            <div className={c(styles.shareButton, styles.shareButtonDesktop)}>
              <DesignButton
                onClick={() => onShareClick()}
                label='Share'
                variant={ButtonVariants.TEXT}
                color={ButtonColors.PURPLE}
                iconNameRight='share'
              />
            </div>
            <div className={c(styles.shareButton, styles.shareButtonMobile)}>
              <DesignButton
                onClick={() => onShareClick()}
                label=''
                variant={ButtonVariants.TEXT}
                color={ButtonColors.PURPLE}
                iconNameRight='share'
              />
            </div>
            <MicrobiomeScoreChartContainer
              kit={currentKit}
              popScore={popGutScore}
              color={color ?? 'gray'}
            />
            <div className='flex flex-column mb-4'>
              <Typography variant='body-m'>{description}</Typography>
              <TextLink
                label='About'
                size='caption'
                iconNameRight='infoCircle'
                onClick={toggleModal}
                className={styles.info}
              />
            </div>
            <TinyAIPromptEntryPoint location='results_mss' variant='inline' />
            {isKitPro && (
              <>
                <div className={styles.divider} />
                <GaugeChartSection />
              </>
            )}
          </div>
        </Card>
        <OffScreenContainer>
          {imageRef ? (
            <MicrobiomeScoreShareImage
              kit={currentKit}
              popScore={popGutScore}
              color={color}
              ref={imageRef}
            />
          ) : null}
        </OffScreenContainer>
      </>
    </CardErrorBoundary>
  );
};
